import { getSpecialtyById } from '@services/queries/specialtyQueries';
import uniq from 'lodash/uniq';

import { UserCreateForm, UserStatus } from 'chaika-frontend-types/frontend-cms';
import { Locale } from 'chaika-enums';

import { OptionType } from '@components/Select';

import { store } from '@store';
import { User } from '@store/models/User';
import { UserApi } from '@api/UserApi';
import { getUniqNewIds } from '@utils/getUniqNewIds';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';

import { Optional } from 'chaika-common-types';

export interface UserMakeOptionTypeParams {
    organizationId?: string;
    onlyDoctors?: boolean;
    withSpecialty?: boolean;
    onlyEpisodeManagers?: boolean;
    withFullName?: boolean;
    withoutTechnical?: boolean;
}

class UserService {
    private isUserLoaded = false;

    public async load(withArchived?: boolean): Promise<void> {
        const archivedLoaded = store.userList.getFilteredValues({ status: UserStatus.Archived });
        const lang = store.authUser.selectedLanguage === Locale.Ru ? undefined : store.authUser.selectedLanguage;

        if (!this.isUserLoaded || (withArchived && archivedLoaded.length === 0)) {
            const users = await UserApi.getList({
                statuses: withArchived ? undefined : UserStatus.Active,
                locale: lang,
            });

            store.userList.addIfNotExist(users);
            this.isUserLoaded = true;
        }
    }

    public async loadIfNotExist(ids: string[]): Promise<void> {
        const userList = store.userList;

        const newIds = getUniqChunkedIds(getUniqNewIds(userList.ids, ids));

        if (newIds.length > 0) {
            const lang = store.authUser.selectedLanguage === Locale.Ru ? undefined : store.authUser.selectedLanguage;
            const result = await Promise.all(newIds.map(ids => UserApi.getList({ ids: ids.join(','), locale: lang })));

            result.forEach(users => {
                userList.add(users);
            });
        }
    }

    public async loadByName(name: string, limit: number = 30) {
        const lang = store.authUser.selectedLanguage === Locale.Ru ? undefined : store.authUser.selectedLanguage;
        const users = await UserApi.getList({
            name,
            statuses: UserStatus.Active,
            limit,
            locale: lang,
        });

        store.userList.addIfNotExist(users);

        return users;
    }

    public async loadProfileData(ids: string[]): Promise<void> {
        const uniqIds = uniq(ids);
        await this.loadIfNotExist(uniqIds);
    }

    public async create(user: UserCreateForm): Promise<void> {
        await UserApi.create(user);
        await this.loadIfNotExist([user.id]);
    }

    public async updateStatus(id: string, status: UserStatus): Promise<void> {
        await UserApi.updateStatus(id, status);
        const user = store.userList.getExisting(id);

        user.status = status;
    }

    public getUser(id: string): Optional<User> {
        return store.userList.get(id);
    }

    public makeOptionTypes(params: UserMakeOptionTypeParams = {}): OptionType<string>[] {
        const { userList } = store;
        const { organizationId, onlyDoctors, onlyEpisodeManagers, withoutTechnical, withFullName, withSpecialty } =
            params;

        const users =
            organizationId || onlyDoctors || onlyEpisodeManagers || withoutTechnical
                ? userList.getFilteredValues({
                      organizationId,
                      withoutTechnical,
                      isDoctor: onlyDoctors,
                      canBeEpisodeManager: onlyEpisodeManagers,
                  })
                : userList.values;

        return users.map(user => ({
            value: user.id,
            label: `${withFullName ? user.fullName : user.shortFullName}${
                withSpecialty && user.mainSpecialtyId ? ` - (${getSpecialtyById(user.mainSpecialtyId)?.name})` : ''
            }`,
        }));
    }

    public reset(): void {
        const { userList } = store;
        userList.reset();
        this.isUserLoaded = false;
    }
}

export const userService = new UserService();
